import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Table, Input, Select, Tag, Typography, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  STATUS_STYLE, fmtMoney, fmtDate,
} from './subscriptionConstants';
import httpInjectorService from 'services/http-injector.service';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

/* Map API status string -> antd Tag color, falling back to STATUS_STYLE if present */
function statusColor(status) {
  const known = { active: 'green', trial: 'blue', expired: 'red', cancelled: 'default', pending: 'gold' };
  return STATUS_STYLE?.[status]?.color || known[status] || 'default';
}
function statusLabel(status) {
  return STATUS_STYLE?.[status]?.label || (status ? status.charAt(0).toUpperCase() + status.slice(1) : '—');
}

/* ──────────────────────────────────────────────────────────────── */
/* SUBSCRIPTIONS VIEW (organization list)                           */
/* ──────────────────────────────────────────────────────────────── */
export function SubscriptionsView({ notify, onOpenOrg, setOrgsCache }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpInjectorService.getOrgSubscription();
      // API shape: { status, message, data: [ { id, org_id, org_name, plan_id,
      //   subscription_type, status, starts_at, expires_at, auto_renew, payment_ref,
      //   plan_name, plan_type, plan_code, price, currency, billing_cycle }, ... ] }
      setOrgs(res.data || res.subscriptions || []);
    } catch (e) {
      notify?.(e.message || 'Could not load subscriptions', 'error');
    } finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOrgsCache?.(orgs); }, [orgs]); // eslint-disable-line

  const filtered = useMemo(() => orgs.filter(o => {
    const q = query.toLowerCase();
    const matchesQuery =
      o.org_name?.toLowerCase().includes(q)
      || String(o.org_id).toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchesQuery && matchesStatus;
  }), [orgs, query, statusFilter]);

  const mrrTotal = useMemo(
    () => orgs
      .filter(o => o.status === 'active' && o.plan_type !== 'free_trial')
      .reduce((s, o) => s + (Number(o.price) || 0), 0),
    [orgs]
  );

  const onTrialCount = useMemo(
    () => orgs.filter(o => o.subscription_type === 'trial').length,
    [orgs]
  );

  const statusOptions = useMemo(() => {
    const set = new Set(orgs.map(o => o.status).filter(Boolean));
    return Array.from(set);
  }, [orgs]);

  /* ── Table column definitions ─────────────────────────────────── */
  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Organization',
      key: 'organization',
      sorter: (a, b) => String(a.org_name || '').localeCompare(String(b.org_name || '')),
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}>
            {record.org_name}
          </div>
          {/* <Text type="secondary" style={{ fontSize: 12 }}>
            ID: {record.org_id}
          </Text> */}
        </div>
      ),
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => (
        <div>
          <div>{record.plan_name}</div>
          {/* <Text type="secondary" style={{ fontSize: 12 }}>
            {[record.plan_type, record.plan_code].filter(Boolean).join(' · ')}
          </Text> */}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'subscription_type',
      key: 'subscription_type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'trial' ? 'blue' : 'purple'} style={{ textTransform: 'capitalize' }}>
          {type}
        </Tag>
      ),
    },
    {
  title: 'Price',
  key: 'price',
  width: 120,
  render: (_, record) => (
    <span style={{ fontFamily: 'monospace' }}>
      {record.price} {record.currency || ''}
    </span>
  ),
},
{
  title: 'Currency',
  dataIndex: 'currency',
  key: 'currency',
  width: 100,
  render: (currency) => currency || '—',
},
    {
      title: 'Cycle',
      dataIndex: 'billing_cycle',
      key: 'billing_cycle',
      width: 100,
      render: (cycle) => <span style={{ textTransform: 'capitalize' }}>{cycle || '—'}</span>,
    },
    {
      title: 'Starts',
      dataIndex: 'starts_at',
      key: 'starts_at',
      width: 120,
      render: (date) => fmtDate(date),
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 120,
      render: (date) => fmtDate(date),
    },
    {
      title: 'Auto Renew',
      dataIndex: 'auto_renew',
      key: 'auto_renew',
      width: 110,
      align: 'center',
      render: (val) => (
        <Tag color={Number(val) === 1 ? 'green' : 'default'}>
          {Number(val) === 1 ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => <Tag color={statusColor(status)}>{statusLabel(status)}</Tag>,
    },
  ];

  return (
    <Card style={{ marginTop: '50px' }}>
      {/* Header Row */}
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            📊 Subscriptions
          </Title>
          <Text type="secondary">Every organization's subscription, at a glance.</Text>
        </Col>
      </Row>

      {/* Summary stats */}
      <Row gutter={16} style={{ marginTop: 20 }}>
        <Col span={8}>
          <Card size="small">
            <Text type="secondary" style={{ fontSize: 12, letterSpacing: '.06em' }}>ORGANIZATIONS</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{orgs.length}</Title>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Text type="secondary" style={{ fontSize: 12, letterSpacing: '.06em' }}>ACTIVE MRR</Text>
            <Title level={3} style={{ margin: '4px 0 0', fontFamily: 'monospace' }}>{fmtMoney(mrrTotal)}</Title>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Text type="secondary" style={{ fontSize: 12, letterSpacing: '.06em' }}>ON TRIAL</Text>
            <Title level={3} style={{ margin: '4px 0 0' }}>{onTrialCount}</Title>
          </Card>
        </Col>
      </Row>

      {/* Search and status filter */}
      <Row style={{ marginTop: 20 }}>
        <Col span={24}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Search
              placeholder="Search by org name or ID…"
              allowClear
              prefix={<SearchOutlined />}
              onSearch={(value) => setQuery(value)}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: '300px' }}
            />
            <Select
              value={statusFilter}
              style={{ width: 160 }}
              onChange={(value) => setStatusFilter(value)}
            >
              <Option value="all">All statuses</Option>
              {statusOptions.map(s => (
                <Option key={s} value={s}>{statusLabel(s)}</Option>
              ))}
            </Select>
            <Select
              defaultValue={10}
              style={{ width: 120 }}
              onChange={(value) => setPageSize(value)}
            >
              <Option value={10}>10 rows</Option>
              <Option value={25}>25 rows</Option>
              <Option value={50}>50 rows</Option>
            </Select>
          </div>
        </Col>
      </Row>

      {/* Table */}
      <div style={{ position: 'relative', zIndex: 0 }}>
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          bordered
          loading={loading}
          pagination={{ pageSize }}
          size="middle"
          scroll={{ x: 1300 }}
          style={{ marginTop: '20px', cursor: 'pointer' }}
          onRow={(record) => ({
            onClick: () => onOpenOrg?.(record.org_id),
          })}
        />
      </div>
    </Card>
  );
}